import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Button, Card, Col, Collapse, Row, Spinner, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faExclamationTriangle,
  faHistory,
  faImage,
  faSync,
  faUser
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import SubtleBadge from "components/common/SubtleBadge";
import useScheduleTokens from "./scheduleTokens";
import ScheduleBadge from "./ScheduleBadge";
import { errorMessage, getAuditHistory } from "./auditApi";

/**
 * Past audits for a patient (INVUI-706). Read-only by design — nothing here
 * offers to edit an audit once it has been submitted.
 */

const formatDateTime = (value) => {
  if (!value) return "—";
  const m = moment(value);
  return m.isValid() ? m.format("MM/DD/YY h:mm A") : String(value);
};

const formatQty = (n) =>
  n === null || n === undefined ? "—" : Number.isInteger(n) ? String(n) : String(Number(Number(n).toFixed(2)));

/* Presigned URLs can be null or expire; either way show a placeholder. */
const AuditImage = ({ url }) => {
  const [failed, setFailed] = useState(false);
  const usable = url && !failed;

  const box = (
    <div
      className="rounded-3 border border-300 bg-body-tertiary d-flex flex-column align-items-center justify-content-center overflow-hidden text-500"
      style={{ width: 120, height: 90 }}
    >
      {usable ? (
        <img
          src={url}
          alt="Audit stock"
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
          onError={() => setFailed(true)}
        />
      ) : (
        <>
          <FontAwesomeIcon icon={faImage} className="fs-7 mb-1" />
          <span className="fs-11">Photo unavailable</span>
        </>
      )}
    </div>
  );

  return usable ? (
    <a href={url} target="_blank" rel="noopener noreferrer" title="Open full size">
      {box}
    </a>
  ) : (
    box
  );
};

AuditImage.propTypes = { url: PropTypes.string };

const AuditEntry = ({ audit, labelOf, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  const variances = audit.varianceCount || 0;

  return (
    <Card className="border border-300 shadow-none mb-3">
      <Card.Body className="p-3">
        <Row className="g-3 align-items-center">
          <Col xs="auto">
            <AuditImage url={audit.imageUrl} />
          </Col>
          <Col>
            <div className="fw-bold text-900 fs-9">{formatDateTime(audit.auditedAt)}</div>
            <div className="text-600 fs-10">
              <FontAwesomeIcon icon={faUser} className="me-1" />
              {audit.auditedBy || "Unknown user"}
            </div>
            <div className="d-flex flex-wrap gap-2 mt-2">
              <SubtleBadge bg="secondary" pill className="d-inline-block">
                {audit.lines.length} medication{audit.lines.length === 1 ? "" : "s"}
              </SubtleBadge>
              <SubtleBadge bg={variances > 0 ? "warning" : "success"} pill className="d-inline-block">
                {variances > 0 ? `${variances} difference${variances === 1 ? "" : "s"}` : "All matched"}
              </SubtleBadge>
            </div>
          </Col>
          {audit.lines.length > 0 && (
            <Col xs={12} sm="auto">
              <Button
                variant="link"
                size="sm"
                className="p-0 fs-10 text-decoration-none"
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
              >
                {open ? "Hide details" : "Show details"}
                <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} className="ms-1" />
              </Button>
            </Col>
          )}
        </Row>

        <Collapse in={open}>
          <div>
            <Table responsive size="sm" className="mb-0 mt-3 fs-10 align-middle">
              <thead>
                <tr className="bg-200 text-900">
                  <th className="ps-2 fs-11 text-uppercase fw-semibold">Medication</th>
                  <th className="fs-11 text-uppercase fw-semibold">Schedule</th>
                  <th className="fs-11 text-uppercase fw-semibold text-center">On record</th>
                  <th className="fs-11 text-uppercase fw-semibold text-center">Counted</th>
                  <th className="fs-11 text-uppercase fw-semibold text-center">Difference</th>
                  <th className="pe-2 fs-11 text-uppercase fw-semibold">Reason</th>
                </tr>
              </thead>
              <tbody>
                {audit.lines.map((l) => (
                  <tr key={l.key}>
                    <td className="ps-2">
                      <div className="fw-semibold text-900">{l.drug_name}</div>
                      {l.strength && <div className="text-600 fs-11">{l.strength}</div>}
                    </td>
                    <td>
                      {l.schedule ? (
                        <ScheduleBadge token={l.schedule} title={labelOf(l.schedule)} />
                      ) : (
                        <span className="text-500">—</span>
                      )}
                    </td>
                    <td className="text-center">
                      {l.no_balance_row ? (
                        <span className="text-600 fs-11">No stock recorded</span>
                      ) : (
                        formatQty(l.expected_qty)
                      )}
                    </td>
                    <td className="text-center fw-semibold">{formatQty(l.counted_qty)}</td>
                    <td className="text-center">
                      {l.variance === null ? (
                        <span className="text-500">—</span>
                      ) : l.variance === 0 ? (
                        <span className="text-success">0</span>
                      ) : (
                        <span className={l.variance < 0 ? "text-danger fw-semibold" : "text-warning fw-semibold"}>
                          {l.variance > 0 ? "+" : ""}
                          {formatQty(l.variance)}
                        </span>
                      )}
                    </td>
                    <td className="pe-2 text-700">{l.reason || <span className="text-500">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Collapse>
      </Card.Body>
    </Card>
  );
};

AuditEntry.propTypes = {
  audit: PropTypes.object.isRequired,
  labelOf: PropTypes.func.isRequired,
  defaultOpen: PropTypes.bool
};

const AuditHistory = ({ patientId, refreshKey }) => {
  const { labelOf } = useScheduleTokens();
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const list = await getAuditHistory(patientId);
      list.sort((a, b) => moment(b.auditedAt).valueOf() - moment(a.auditedAt).valueOf());
      setAudits(list);
    } catch (err) {
      setError(errorMessage(err, "Audit history could not be loaded."));
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  return (
    <div className="pt-2">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h6 className="mb-1 fw-bold text-900 fs-9">
            <FontAwesomeIcon icon={faHistory} className="text-primary me-2" />
            Audit history
          </h6>
          <p className="text-600 mb-0 fs-10">
            Past audits are a permanent record and can&apos;t be changed.
          </p>
        </div>
        <Button variant="outline-secondary" size="sm" className="border-300" onClick={load} disabled={loading}>
          <FontAwesomeIcon icon={faSync} spin={loading} className="me-1" />
          Refresh
        </Button>
      </div>

      {loading && audits.length === 0 ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" role="status" />
          <p className="mt-3 text-600 fs-10 mb-0">Loading audit history...</p>
        </div>
      ) : error ? (
        <Alert variant="danger" className="d-flex align-items-start border-0 rounded-3 fs-10">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-3 mt-1 fs-9" />
          <div className="flex-1">
            <div className="fw-bold">We couldn&apos;t load the audit history</div>
            <div>{error}</div>
          </div>
          <Button variant="outline-danger" size="sm" className="ms-3 rounded-pill px-3" onClick={load}>
            Retry
          </Button>
        </Alert>
      ) : audits.length === 0 ? (
        <div className="text-center py-5 px-3 border border-300 border-dashed rounded-3">
          <FontAwesomeIcon icon={faHistory} className="text-400 fs-5 mb-3" />
          <h6 className="text-900 fw-bold mb-1 fs-9">No audits yet</h6>
          <p className="text-600 fs-10 mb-0">Completed audits for this patient will appear here.</p>
        </div>
      ) : (
        audits.map((a, i) => <AuditEntry key={a.key} audit={a} labelOf={labelOf} defaultOpen={i === 0} />)
      )}
    </div>
  );
};

AuditHistory.propTypes = {
  patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  refreshKey: PropTypes.number
};

export default AuditHistory;
